import { useCallback, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { salvarArquivoBaixado } from '@/lib/http_download';
import { baixarRelatorioPreliminar } from '@/lib/pre_analise';

interface UseBaixarRelatorio {
  /** `id` do caso cujo download está em voo, ou `null`. */
  emVoo: string | null;
  baixar: (caseId: string) => Promise<void>;
}

/**
 * Baixa o Relatório Preliminar de um caso da lista (E72.F2).
 *
 * Vive fora da `CasosPage` pela mesma razão do `usePrioridadeCaso`: a página já
 * carrega, filtra, pagina e ordena, e cada ação de linha que entra nela empurra
 * o arquivo contra o limite do ESLint.
 *
 * O documento é re-renderizado sob demanda no servidor (ADR-0074 §2.6), então a
 * espera é real — daí o `emVoo`, que desabilita o item do menu enquanto o PDF é
 * montado. O nome do arquivo vem do `Content-Disposition`.
 */
export function useBaixarRelatorio(
  client: AxiosInstance,
  aoFalhar: () => void,
): UseBaixarRelatorio {
  const [emVoo, setEmVoo] = useState<string | null>(null);

  const baixar = useCallback(
    async (caseId: string): Promise<void> => {
      setEmVoo(caseId);
      try {
        salvarArquivoBaixado(await baixarRelatorioPreliminar(client, caseId));
      } catch {
        aoFalhar();
      } finally {
        setEmVoo(null);
      }
    },
    [aoFalhar, client],
  );

  return { emVoo, baixar };
}
